import {
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSessionStorage } from 'usehooks-ts';

import {
  StatusIcon,
  StatusIconProps,
} from '@/components/DataDisplay/StatusIcon';
import {
  ActionButton,
  ListActionItemContainer,
} from '@/components/Form/ActionList/ActionList.styled';
import { TabEnum } from '@/components/PageForm/MoreInfoForm/components/AdultChildToggle';

import { SS_MORE_INFO_SOURCE_TAB } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLivenessrecordDisabled } from '@/redux/configs/selectors';
import { selectUser } from '@/redux/user-data/selectors';

import { DocumentStatus } from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import { checkEkycDocumentsStatus, isEkycStatusComplete } from '@/utils/user';

export function IdentityCheck() {
  const { t } = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();
  const [, setMoreInfoSourceTab] = useSessionStorage<TabEnum | ''>(
    SS_MORE_INFO_SOURCE_TAB,
    '',
  );
  const user = useAppSelector(selectUser);
  const { documents = [], clientReviewed } = user || {};
  const isRecordDisabled = useAppSelector(selectLivenessrecordDisabled);

  const ekycStatus = useMemo(
    () => checkEkycDocumentsStatus(documents),
    [documents],
  );
  const isIdentityComplete = isEkycStatusComplete(ekycStatus, isRecordDisabled);

  const onClickIdentityCheck = () => {
    setMoreInfoSourceTab(TabEnum.Adult);

    if (!ekycStatus.isIDChecked) {
      navigate(ROUTE.VERIFY_ID, {
        replace: true,
        state: { notNavigateByStatus: true },
      });
    } else {
      navigate(ROUTE.VERIFY_SELFIE, {
        replace: true,
        state: { notNavigateByStatus: true },
      });
    }
  };

  const getIconProps = (): StatusIconProps => {
    if (isIdentityComplete) {
      return { value: DocumentStatus.Approved };
    }

    return { iconName: 'arrow-right', value: '' };
  };

  const isDisabledButton = useMemo(() => {
    if (!clientReviewed) {
      return true;
    }

    return isIdentityComplete;
  }, [clientReviewed, isIdentityComplete]);

  return (
    <ListActionItemContainer
      {...getDataTestId(`list-action-item-personalDetails`)}
      disabled={!clientReviewed}
    >
      <ListItemText
        primary={
          <Typography variant="h5">
            {t('moreInfo.form.identityCheck.title')}
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            {...(isIdentityComplete && {
              color: theme.palette.status[DocumentStatus.Approved].text,
            })}
          >
            {isIdentityComplete
              ? t('moreInfo.form.identityCheck.status.completed')
              : t('moreInfo.form.identityCheck.status.pending')}
          </Typography>
        }
      />
      <ListItemButton>
        <ActionButton
          {...getDataTestId(`action-button`)}
          onClick={onClickIdentityCheck}
          disabled={isDisabledButton}
        >
          <StatusIcon {...getIconProps()} />
        </ActionButton>
      </ListItemButton>
    </ListActionItemContainer>
  );
}
